/**
 * Sign in, create an account, or reset a password -- on Birbly's own screen.
 *
 * Replaces the trip to Cognito's hosted page for email and password
 * (2026-10-19). In the app that trip opened the system browser, and to a
 * parent it looked like leaving Birbly. Now nothing leaves: the form talks to
 * Cognito's API (auth/direct.ts) and the password is proved with SRP, never
 * sent. Google and Apple, which have to go through a browser, are the buttons
 * under the form.
 *
 * One screen with a few steps rather than a page per step, so Back always
 * means "the step before" and the email typed once is never asked for again.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import {
  answerCode,
  CognitoError,
  confirmRegistration,
  register,
  resendConfirmation,
  setNewPassword,
  signInWithPassword,
  startReset,
  finishReset,
  type SignInStep,
} from '../auth/direct';
import { finishDirectSignIn } from '../auth/cognito';
import { BackButton } from '../components/BackButton';

type Step = 'sign-in' | 'sign-up' | 'confirm' | 'forgot' | 'reset' | 'code' | 'new-password';

const MIN_PASSWORD = 12;

export function SignInScreen({
  start,
  onBack,
  social,
}: {
  start: 'sign-in' | 'sign-up';
  /** Out of the form altogether, to the screen that opened it. */
  onBack: () => void;
  /** Continue with Google / Apple, which still go through a browser sheet. */
  social?: ReactNode;
}) {
  const [step, setStep] = useState<Step>(start);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [working, setWorking] = useState(false);
  const [pending, setPending] = useState<SignInStep | null>(null);

  function go(next: Step, message = ''): void {
    setStep(next);
    setError('');
    setNote(message);
    setCode('');
  }

  /** Where a sign-in attempt leads: in, or to the step Cognito asks for. */
  function follow(result: SignInStep): void {
    if (result.kind === 'done') {
      finishDirectSignIn(result.tokens);
      return;
    }
    setPending(result);
    if (result.kind === 'code') {
      go('code', result.challenge === 'SOFTWARE_TOKEN_MFA'
        ? 'Enter the code from your authenticator app.'
        : `We sent a code to ${result.destination ?? 'you'}.`);
    } else {
      setPassword('');
      go('new-password', 'Choose a new password to finish signing in.');
    }
  }

  async function run(work: () => Promise<void>): Promise<void> {
    setWorking(true);
    setError('');
    try {
      await work();
    } catch (problem) {
      if (problem instanceof CognitoError && problem.code === 'UserNotConfirmedException') {
        await resendConfirmation(email).catch(() => undefined);
        go('confirm', 'Your email is not confirmed yet. We sent a new code to it.');
        return;
      }
      setError(problem instanceof Error ? problem.message : 'Something went wrong. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (working) return;
    void run(async () => {
      switch (step) {
        case 'sign-in':
          follow(await signInWithPassword(email, password));
          return;
        case 'sign-up': {
          const made = await register(email, password);
          if (made.confirmed) follow(await signInWithPassword(email, password));
          else go('confirm', `We sent a code to ${made.destination ?? email}. It may take a minute.`);
          return;
        }
        case 'confirm':
          await confirmRegistration(email, code);
          // Straight in with the password they just chose; nothing to retype.
          if (password) follow(await signInWithPassword(email, password));
          else go('sign-in', 'Email confirmed. Sign in to carry on.');
          return;
        case 'forgot': {
          const where = await startReset(email);
          setPassword('');
          go('reset', `We sent a code to ${where ?? email}.`);
          return;
        }
        case 'reset':
          await finishReset(email, code, password);
          follow(await signInWithPassword(email, password));
          return;
        case 'code':
          if (pending?.kind === 'code') follow(await answerCode(pending, code));
          return;
        case 'new-password':
          if (pending?.kind === 'new-password') follow(await setNewPassword(pending, password));
          return;
      }
    });
  }

  const back = (): void => {
    if (step === 'sign-in' || step === 'sign-up') onBack();
    else if (step === 'reset') go('forgot');
    else go(step === 'confirm' ? 'sign-up' : 'sign-in');
  };

  const heading: Record<Step, string> = {
    'sign-in': 'Welcome back',
    'sign-up': 'Create your account',
    confirm: 'Check your email',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
    code: 'One more step',
    'new-password': 'Choose a new password',
  };

  const wantsEmail = step === 'sign-in' || step === 'sign-up' || step === 'forgot';
  const wantsPassword = step === 'sign-in' || step === 'sign-up' || step === 'reset' || step === 'new-password';
  const wantsCode = step === 'confirm' || step === 'reset' || step === 'code';
  const newPassword = step !== 'sign-in';
  const short = wantsPassword && newPassword && password.length > 0 && password.length < MIN_PASSWORD;
  const ready =
    !working &&
    (!wantsEmail || /\S+@\S+\.\S+/.test(email.trim())) &&
    (!wantsPassword || password.length >= (newPassword ? MIN_PASSWORD : 1)) &&
    (!wantsCode || code.trim().length >= 4);

  const action: Record<Step, string> = {
    'sign-in': 'Sign in',
    'sign-up': 'Create account',
    confirm: 'Confirm',
    forgot: 'Send me a code',
    reset: 'Save and sign in',
    code: 'Continue',
    'new-password': 'Save and sign in',
  };

  return (
    <div className="sign-in">
      <BackButton label="Back" onClick={back} />
      <h2>{heading[step]}</h2>
      {note ? <p className="sign-in-note" role="status">{note}</p> : null}

      <form onSubmit={submit} noValidate aria-busy={working}>
        {wantsEmail ? (
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
        ) : null}

        {wantsCode ? (
          <label className="field">
            <span>Code</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={8}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              required
            />
          </label>
        ) : null}

        {wantsPassword ? (
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              autoComplete={newPassword ? 'new-password' : 'current-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={newPassword ? MIN_PASSWORD : undefined}
              required
            />
            {newPassword ? (
              <small className={short ? 'warn' : undefined}>At least {MIN_PASSWORD} characters. A short sentence works well.</small>
            ) : null}
          </label>
        ) : null}

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="actions stacked">
          <button type="submit" className="primary full" disabled={!ready} aria-busy={working}>
            {working ? 'One moment…' : action[step]}
          </button>
          {step === 'sign-in' || step === 'sign-up' ? social : null}
          {step === 'sign-in' ? (
            <>
              <button type="button" className="text-button" onClick={() => go('forgot')}>
                Forgot your password?
              </button>
              <button type="button" className="text-button" onClick={() => go('sign-up')}>
                New here? Create an account
              </button>
            </>
          ) : null}
          {step === 'sign-up' ? (
            <button type="button" className="text-button" onClick={() => go('sign-in')}>
              Already have an account? Sign in
            </button>
          ) : null}
          {step === 'confirm' ? (
            <button
              type="button"
              className="text-button"
              disabled={working}
              onClick={() => void run(async () => {
                await resendConfirmation(email);
                setNote('We sent a new code.');
              })}
            >
              Send a new code
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
